/**
 * Canvas 2-D renderer for a linkage and the curves around it. Owns the
 * world<->screen transform (pan/zoom, y-up) and nothing else — no app state.
 */

import type { MechanismSpec } from "../kinematics/types.js";
import { solvePosition } from "../kinematics/solve.js";
import type { P2 } from "../synthesis/curve.js";

export interface Palette {
  bg: string;
  grid: string;
  gridAxis: string;
  ground: string;
  bar: string;
  crank: string;
  joint: string;
  jointFill: string;
  coupler: string;
  couplerPoint: string;
  trace: string;
  target: string;
  ghost: string;
  text: string;
}

export const LIGHT: Palette = {
  bg: "#f7f7f4",
  grid: "#e6e6df",
  gridAxis: "#d0d0c4",
  ground: "#8a8a7a",
  bar: "#2b2b2b",
  crank: "#c2410c",
  joint: "#2b2b2b",
  jointFill: "#ffffff",
  coupler: "#1d4ed8",
  couplerPoint: "#1d4ed8",
  trace: "#1d4ed8",
  target: "#059669",
  ghost: "#b9b9ad",
  text: "#3a3a34",
};

export const DARK: Palette = {
  bg: "#14140f",
  grid: "#26261f",
  gridAxis: "#35352b",
  ground: "#7c7c6a",
  bar: "#e8e8e0",
  crank: "#fb923c",
  joint: "#e8e8e0",
  jointFill: "#14140f",
  coupler: "#60a5fa",
  couplerPoint: "#60a5fa",
  trace: "#60a5fa",
  target: "#34d399",
  ghost: "#3f3f36",
  text: "#c9c9bd",
};

export interface View {
  /** World units per CSS pixel. */
  scale: number;
  /** World coordinate at the canvas centre. */
  cx: number;
  cy: number;
}

export class CanvasView {
  readonly ctx: CanvasRenderingContext2D;
  view: View = { scale: 0.02, cx: 0, cy: 0 };
  palette: Palette = LIGHT;
  private width = 0;
  private height = 0;
  private dpr = 1;

  constructor(readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
  }

  resize(): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
  }

  /** World -> CSS-pixel. */
  toScreen(p: P2): P2 {
    return [
      this.width / 2 + (p[0] - this.view.cx) / this.view.scale,
      this.height / 2 - (p[1] - this.view.cy) / this.view.scale,
    ];
  }

  /** CSS-pixel -> world. */
  toWorld(px: number, py: number): P2 {
    return [
      this.view.cx + (px - this.width / 2) * this.view.scale,
      this.view.cy - (py - this.height / 2) * this.view.scale,
    ];
  }

  zoomAt(px: number, py: number, factor: number): void {
    const before = this.toWorld(px, py);
    this.view.scale = Math.max(1e-4, Math.min(5, this.view.scale * factor));
    const after = this.toWorld(px, py);
    this.view.cx += before[0] - after[0];
    this.view.cy += before[1] - after[1];
  }

  panPixels(dx: number, dy: number): void {
    this.view.cx -= dx * this.view.scale;
    this.view.cy += dy * this.view.scale;
  }

  /** Fit a set of world points into view with margin. */
  fit(points: P2[], marginFrac = 0.25): void {
    if (points.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of points) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    const w = Math.max(maxX - minX, 1e-6);
    const h = Math.max(maxY - minY, 1e-6);
    this.view.cx = (minX + maxX) / 2;
    this.view.cy = (minY + maxY) / 2;
    const sx = (w * (1 + marginFrac)) / Math.max(this.width, 1);
    const sy = (h * (1 + marginFrac)) / Math.max(this.height, 1);
    this.view.scale = Math.max(sx, sy, 1e-4);
  }

  private begin(): void {
    const { ctx } = this;
    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.fillStyle = this.palette.bg;
    ctx.fillRect(0, 0, this.width, this.height);
  }

  private end(): void {
    this.ctx.restore();
  }

  clear(): void {
    this.begin();
    this.drawGrid();
    this.end();
  }

  drawGrid(): void {
    const { ctx } = this;
    const step = niceStep(this.view.scale * 90);
    const tl = this.toWorld(0, 0);
    const br = this.toWorld(this.width, this.height);
    ctx.lineWidth = 1;
    ctx.strokeStyle = this.palette.grid;
    ctx.beginPath();
    for (let x = Math.ceil(tl[0] / step) * step; x < br[0]; x += step) {
      const s = this.toScreen([x, 0]);
      ctx.moveTo(s[0], 0);
      ctx.lineTo(s[0], this.height);
    }
    for (let y = Math.ceil(br[1] / step) * step; y < tl[1]; y += step) {
      const s = this.toScreen([0, y]);
      ctx.moveTo(0, s[1]);
      ctx.lineTo(this.width, s[1]);
    }
    ctx.stroke();
    // axes
    ctx.strokeStyle = this.palette.gridAxis;
    ctx.beginPath();
    const ox = this.toScreen([0, 0]);
    ctx.moveTo(ox[0], 0);
    ctx.lineTo(ox[0], this.height);
    ctx.moveTo(0, ox[1]);
    ctx.lineTo(this.width, ox[1]);
    ctx.stroke();
  }

  polyline(
    pts: P2[],
    color: string,
    width: number,
    closed = false,
    dash: number[] = [],
    breakJumps = false,
  ): void {
    if (pts.length < 2) return;
    const { ctx } = this;

    // Optionally lift the pen across large gaps (assembly-mode breaks) so the
    // curve is not drawn through configurations the mechanism cannot reach.
    let cut = Infinity;
    if (breakJumps && pts.length > 6) {
      const steps: number[] = [];
      for (let i = 1; i < pts.length; i++) {
        steps.push(Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
      }
      const sorted = [...steps].sort((a, b) => a - b);
      const median = sorted[sorted.length >> 1] || 1e-9;
      cut = Math.max(median * 10, median + 1e-9);
    }

    ctx.save();
    ctx.setLineDash(dash);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    const p0 = this.toScreen(pts[0]);
    ctx.moveTo(p0[0], p0[1]);
    for (let i = 1; i < pts.length; i++) {
      const jump = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
      const p = this.toScreen(pts[i]);
      if (jump > cut) ctx.moveTo(p[0], p[1]);
      else ctx.lineTo(p[0], p[1]);
    }
    if (closed && cut === Infinity) ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }

  dot(p: P2, r: number, fill: string, stroke?: string): void {
    const { ctx } = this;
    const s = this.toScreen(p);
    ctx.beginPath();
    ctx.arc(s[0], s[1], r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  groundSymbol(p: P2): void {
    const { ctx } = this;
    const s = this.toScreen(p);
    ctx.save();
    ctx.strokeStyle = this.palette.ground;
    ctx.fillStyle = this.palette.ground;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(s[0] - 9, s[1] + 8);
    ctx.lineTo(s[0] + 9, s[1] + 8);
    for (let i = -8; i <= 8; i += 5) {
      ctx.moveTo(s[0] + i, s[1] + 8);
      ctx.lineTo(s[0] + i - 5, s[1] + 15);
    }
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Render everything. `driverAngle` positions the animated pose; pass the
   * current numeric solution if you have one to avoid re-solving.
   */
  render(opts: {
    spec: MechanismSpec | null;
    driverAngle: number;
    trace?: P2[] | null;
    target?: P2[] | null;
    ghostTrace?: P2[] | null;
    drawingPath?: P2[] | null;
    showLabels?: boolean;
  }): void {
    this.begin();
    this.drawGrid();

    if (opts.ghostTrace && opts.ghostTrace.length > 1) {
      this.polyline(opts.ghostTrace, this.palette.ghost, 1.5, true);
    }
    if (opts.target && opts.target.length > 1) {
      this.polyline(opts.target, this.palette.target, 2.5, false, [7, 5]);
    }
    if (opts.drawingPath && opts.drawingPath.length > 1) {
      this.polyline(opts.drawingPath, this.palette.target, 2.5, false);
    }
    if (opts.trace && opts.trace.length > 1) {
      this.polyline(opts.trace, this.palette.trace, 2, true, [], true);
    }

    if (opts.spec) this.drawMechanism(opts.spec, opts.driverAngle, opts.showLabels ?? false);
    this.end();
  }

  drawMechanism(spec: MechanismSpec, driverAngle: number, labels: boolean): void {
    const { ctx } = this;
    const G = spec.grounds.length;
    const seed = spec.free.flatMap((f) => f.guess);
    const sol = solvePosition(spec, driverAngle, seed, { maxIterations: 60 });
    const pos = sol.positions;

    // bars
    for (const [ia, ib] of spec.bars) {
      this.polyline([pos[ia] as P2, pos[ib] as P2], this.palette.bar, 3.5);
    }
    // crank (driver base -> driven joint)
    this.polyline(
      [spec.grounds[spec.driver.base] as P2, pos[G] as P2],
      this.palette.crank,
      4,
    );
    // coupler point offset marker (from bar to tracer)
    const [ca, cb] = spec.bars[spec.coupler.bar];
    const a = pos[ca];
    const dx = pos[cb][0] - a[0];
    const dy = pos[cb][1] - a[1];
    const len = Math.hypot(dx, dy) || 1e-9;
    const ux = dx / len;
    const uy = dy / len;
    const cp: P2 = [
      a[0] + spec.coupler.u * ux + spec.coupler.v * -uy,
      a[1] + spec.coupler.u * uy + spec.coupler.v * ux,
    ];
    this.polyline([a as P2, cp, pos[cb] as P2], this.palette.coupler, 1.5, false, [4, 3]);

    // joints
    for (let i = 0; i < G; i++) this.groundSymbol(spec.grounds[i] as P2);
    for (let i = 0; i < G; i++) this.dot(spec.grounds[i] as P2, 5, this.palette.jointFill, this.palette.ground);
    this.dot(pos[G] as P2, 5, this.palette.jointFill, this.palette.crank);
    for (let i = G + 1; i < pos.length; i++) {
      this.dot(pos[i] as P2, 5, this.palette.jointFill, this.palette.joint);
    }
    this.dot(cp, 6, this.palette.couplerPoint, this.palette.bg);

    if (!sol.converged) {
      const s = this.toScreen(cp);
      ctx.fillStyle = this.palette.crank;
      ctx.font = "12px system-ui, sans-serif";
      ctx.fillText("locked", s[0] + 10, s[1]);
    }

    if (labels) {
      ctx.fillStyle = this.palette.text;
      ctx.font = "11px ui-monospace, monospace";
      const names = ["O2", "O4", "O6"];
      for (let i = 0; i < G; i++) {
        const s = this.toScreen(spec.grounds[i] as P2);
        ctx.fillText(names[i] ?? `O${i}`, s[0] + 8, s[1] + 18);
      }
      const sa = this.toScreen(pos[G] as P2);
      ctx.fillText("A", sa[0] + 8, sa[1] - 8);
      const sp = this.toScreen(cp);
      ctx.fillText("P", sp[0] + 10, sp[1] + 4);
    }
  }
}

function niceStep(target: number): number {
  const pow = Math.pow(10, Math.floor(Math.log10(target)));
  const n = target / pow;
  const m = n < 1.5 ? 1 : n < 3.5 ? 2 : n < 7.5 ? 5 : 10;
  return m * pow;
}
